/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../_models/user';
import { environment } from '../../environments/environment';
import { ApiUriContants } from '../_constants/apiUriConstants';
import { HttpService } from './http.service';
import { InterceptorSkipHeader } from '../_constants/authConstants';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { DataShareService } from './data-share.service';
import { clearLocalStorageExceptCharts } from '../_helpers/window.helper';
import { resolveApiBaseUrl } from '../_helpers/api-base-url';

/**
 * Common authentication service.
 * Should be used to as an interlayer between UI Components and Auth Strategy.
 */
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  //private adalSvc: MsAdalAngular6Service,
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  logoutReturnUrl: string = "/auth/login";

  constructor(
    private http: HttpClient,
    private httpService: HttpService,
    private cookieService: CookieService,
    private dataShareService: DataShareService,
    private router: Router
  ) {
    var localStorageString=localStorage.getItem('currentUser');
    this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorageString?localStorageString:"{}"));
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  checkSSOEnabled(userName:any){
    const url = `${ApiUriContants.account}getcheckssoenabled`;
    return this.httpService.get(url, { username: userName });
  }

  loginWithSSO(userObj:any) {
    const URL = `${ApiUriContants.account}loginwithactivedirectory`;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .post<any>(
        `${resolveApiBaseUrl(URL)}/${URL}`,
        JSON.stringify(userObj),
        { headers: headers }).pipe(
          map(
            user => {
              // login successful if there's a jwt token in the response
              if (user && user.token) {
                user.isLoginWithSSO = true;
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('currentUser', JSON.stringify(user));
                this.currentUserSubject.next(user);
              }
              return user;
            },
            (error:any) => {
              return error;
            }));
  }

  login(params:any, type = 'userNameAndPassword') {
    const formData = new FormData();
    var authServerLoginUrl = environment.authServerLoginWithGeoOneSsoApi;
    if (type === 'userNameAndPassword') {
      authServerLoginUrl = environment.authServerLoginApi;
      formData.append('Username', params.username);
      formData.append('Password', params.password);
    } else if (type === 'Sso') {
      authServerLoginUrl = environment.authServerLoginWithSsoApi;
      formData.append('email', params.email);
    }

    const headers = (type === 'Sso' || type === 'GeoOneSso')
      ? new HttpHeaders({
          Authorization: `Bearer ${params.token}`
        })
      : new HttpHeaders().set(InterceptorSkipHeader, '');
    
    return this.http.post<any>(
      authServerLoginUrl,
      formData,
      {
        headers: headers,
        withCredentials: true
      }
    );
  }

  forgotPassword(username: string, recaptchaToken: string) {
    const URL = 'api/user/Account/forgotpassword';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      [InterceptorSkipHeader]: ''
    })
    return this.http.post<any>(
      `${resolveApiBaseUrl(URL)}/${URL}`,
      JSON.stringify({ username: username, recaptchaToken: recaptchaToken }),
      { headers: headers },
    );
  }

  checktokenexistnotexpired(token: string) {
    const URL = 'api/user/Account/checktokenexistnotexpired/?token=' + token;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http.get<any>(`${resolveApiBaseUrl(URL)}/${URL}`).pipe(
      map(
        res => {
          return res;
        },
        (error:any) => {
          return error;
        }));
  }

  resetPassword(password: string, token: string) {
    const tokenInfo = {
      password: password,
    };

    const URL = 'api/user/Account/resetpassword';
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      [InterceptorSkipHeader]: ''
    });
    headers = headers.append('Authorization', 'Bearer ' + token);
    const options = {
      headers: headers,
      params: {
        token: token,
      },
    };
    return this.http
      .post<any>(`${resolveApiBaseUrl(URL)}/${URL}`, tokenInfo, options)
      .pipe(
        map(
          res => {
            return res;
          },
          (error:any) => {
            return error;
          }));
  }

  sendVerificationCode(username: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const URL = 'api/user/Account/SendVerificationCode';
    return this.http.post<any>(`${resolveApiBaseUrl(URL)}/${URL}`, { userName: username }, {
      headers: headers,
      observe: 'response',
    });
  }

  verifyCode(username: string, code: string) {
    const URL = 'api/user/Account/LoginWithVerificationCode';
    const codeObj = {
      username: username,
      code: code,
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.http
      .post<any>(
        `${resolveApiBaseUrl(URL)}/${URL}`,
        JSON.stringify(codeObj),
        { headers: headers },
      )
      .pipe(
        map(
          user => {
            if (user && user.token) {
              localStorage.setItem('currentUser', JSON.stringify(user));
              this.currentUserSubject.next(user);
            }
            return user;
          },
          (error:any) => {
            return error;
          }));
  }

 getTenantConfig(email: string) {
  const url = 'api/auth/tenant-config';
  return this.httpService.get("api/auth/tenant-config", { email: email });
}
  getUserDetails(username:any) {
    const url = `${ApiUriContants.account}getusermenudetails`;
    return this.httpService.get(url, { username: username });
  }
  sendVerificationCodeNew(data:any) {
    const URL = `${ApiUriContants.account}SendVerificationCode`;
    return this.httpService.post(URL, data);
  }
  setAdUserInCache(username:any) {
    const url = `${ApiUriContants.account}setaduserincache`;
    return this.httpService.get(url, { userName: username });
  }
  
  redeemCodeForToken(authority: string, body: string): Observable<any> {
    const url = `${authority}/oauth2/v2.0/token`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    
    return this.http.post<any>(url, body, { headers });
  }

  async logout(): Promise<any> {
    try {
      await this.logoutAuth();
    } catch (err) {
      // Optionally log error
      console.error('Logout failed in the authservice', err);
    } finally {
      this.currentUserSubject.next(null as any);
      this.dataShareService.isMenuListSuccess = false;
      this.dataShareService.userPolicyList = [];
      clearLocalStorageExceptCharts();
      sessionStorage.clear();
      this.cookieService.deleteAll(); // Only affects non-HttpOnly cookies
      this.router.navigate([this.logoutReturnUrl]);
    }
  }

  async logoutAuth(): Promise<any> {
    return await firstValueFrom(
      this.http.post(
        environment.authServerLogoutApi,
        {},
        {
          headers: new HttpHeaders().set(InterceptorSkipHeader, ''),
          withCredentials: true,
          responseType: 'text'
        }
      )
    );
  }
  checkUserNameOrEmail(username: string) {
    const URL = 'api/user/Account/checkusernameoremail';

    return this.http.get(`${resolveApiBaseUrl(URL)}/${URL}`, {
      params: { Username: username },
      responseType: 'text'
    });
}

}